import React, { useState, useEffect } from 'react';
import { Typography, Button, Skeleton, Empty } from 'antd';
import { 
  UpOutlined, 
  DownOutlined, 
  ArrowLeftOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import { RootState } from '../../redux';
import type { AppDispatch } from '../../redux';
import './CandidateCard.css';
import { Candidate } from '../../data/candidate';
import { matchCandidates } from '../../redux/analysisSlice';
import CandidateCard from './CandidateCard';
import AvailabilityPreview from '../availability/AvailabilityPreview';

const { Title, Paragraph } = Typography;

interface Props {
  onBack: () => void;
}

const SearchCandidates: React.FC<Props> = ({ onBack }) => {
  const [candidatesByCompany, setCandidatesByCompany] = useState<{ [key: string]: { companyId: string; candidates: Candidate[] } }>({});
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [expandedProfile, setExpandedProfile] = useState(false);
  const [expandedCompanies, setExpandedCompanies] = useState<string[]>([]);
  const { candidates, companies, profile } = useSelector((state: RootState) => state.analysis);
  const [activeCandidate, setActiveCandidate] = useState<Candidate | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  
  const staticCandidates = candidates.candidateFilterResults;

  useEffect(() => {
    const fetchCandidates = async () => {
        if (!profile.result) return;
        try {
          await dispatch(matchCandidates({ decision_criteria: profile.result, companies: companies.allCompanies.filter(company => company.selected === true), match_id: companies.matchId})).unwrap();
        } catch (err) {
            console.error("Error matching companies:", err);
        }
    };

    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (!staticCandidates?.filtered) {
      return;
    }

    const grouped: { [key: string]: { companyId: string; candidates: Candidate[] } } = {};
    let count = 0;

    staticCandidates.filtered.forEach(candidateInput => {
      const candidate = candidateInput as unknown as Candidate; 
      const companyId = candidate.company_id;

      if (!grouped[companyId]) {
        grouped[companyId] = {
          companyId,
          candidates: [],
        };
      }
      
      grouped[companyId].candidates.push(candidate);
      count++;
    });

    setCandidatesByCompany(grouped);
    setTotalCandidates(count);
  }, [staticCandidates?.filtered]);

  const truncateText = (text: string, maxLength?: number) => {
    if (!text) return '';
    text = text.split('\n')[0];
    if (!maxLength)
        return text;
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const getMatchScore = (candidateId: string) => {
    const reason = (staticCandidates.reasons_include as Record<string, string>)[candidateId];
    if (!reason) return 0;
    
    const match = reason.match(/Score ([\d.]+):/);
    return match ? parseFloat(match[1]) : 0;
  };

  const openCandidateModal = (candidate: Candidate) => {
    setActiveCandidate(candidate);
    setModalVisible(true);
    document.body.style.overflow = 'hidden';
  };

  const closeCandidateModal = () => {
    setModalVisible(false);
    setActiveCandidate(null);
    document.body.style.overflow = 'auto';
  };

  const toggleCompanyExpand = (companyId: string) => {
    setExpandedCompanies(prev => 
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const resetComponent = () => {
    setTotalCandidates(0);
    onBack();
  };

  return (
    <>
      <div className="matches-list">
        <Title level={4} className="panel-title">Searching Candidates</Title>
        <div className="llm-reasoning-container">
          <p className="label">AI Interpretation</p>
          <Paragraph className="llm-reasoning">
            <div className="service-offerings-preview" style={{ display: 'flex' }}>
              <div className="preview-content">
                {expandedProfile ? (
                  <ReactMarkdown>{profile.result || "No profile analysis available"}</ReactMarkdown>
                ) : (
                  <ReactMarkdown>{truncateText(profile.result) || "No profile analysis available"}</ReactMarkdown>
                )}
              </div>
              <Button
                type="text"
                icon={expandedProfile ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setExpandedProfile(!expandedProfile)}
                className="material-button"
              />
            </div>
          </Paragraph>
        </div>
        <Paragraph style={{ color: "#6b7280"}}>
          Candidates matching your criteria from selected companies
        </Paragraph>
        {candidates.loading === 'idle' || candidates.loading === 'pending' ? (
          <div className="company-grid">
            {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="company-card skeleton">
                    <div className="card-header">
                        <Skeleton.Input active size="small" style={{ marginBottom: '8px' }} />
                        <Skeleton.Input active size="small" />
                    </div>
                    <Skeleton.Input active size="small" style={{ width: '100%', height: 40 }} />
                </div>
            ))}
          </div>
        ) : totalCandidates === 0 ? (
          <Empty
            description="No candidates found"
            className="empty-state"
          />
        ) : (
          <div className="company-accordion">
            {Object.entries(candidatesByCompany)
              .filter(([_, group]) => group.candidates.length > 0)
              .map(([companyId, group]) => {
                const companyInfo = companies.allCompanies.find(c => c._id === companyId);
                const companyName = companyInfo?.name || companyId;
                const isExpanded = expandedCompanies.includes(companyId);

                return (
                  <div key={companyId} className="company-accordion-item">
                    <div 
                      className="company-row" 
                      onClick={() => toggleCompanyExpand(companyId)}
                    >
                      <div className="company-row-name">
                        <span className="expand-icon">
                          {isExpanded ? <DownOutlined /> : <RightOutlined />}
                        </span>
                        {companyName}
                      </div>
                      <div className="company-row-count">
                        {group.candidates.length} {group.candidates.length === 1 ? 'candidate' : 'candidates'}
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="candidate-cards">
                        {group.candidates.map(candidate => {
                          return (
                            <div 
                              key={candidate._id}
                              className="company-card"
                              onClick={() => openCandidateModal(candidate)}
                            >
                              <div className="card-header">
                                <div className="company-info">
                                  <div className="company-name">{candidate.cv?.full_name || candidate.name}</div>
                                  <div className="company-score">
                                    <span className="score-pill">{Math.round(getMatchScore(candidate._id) * 100)}%</span>
                                    <span className="score-label">Match Score</span>
                                  </div>
                                </div>
                              </div>
                              <div className="card-content">
                                <div className="info-row">
                                  <span className="info-label">Position:</span>
                                  <span className="info-value">{truncateText(candidate.cv?.personal_info?.position, 35) || 'N/A'}</span>
                                </div>
                                {candidate.cv?.personal_info?.competence && (
                                  <div className="info-row competence-row">
                                    <span className="info-label">Competence:</span>
                                    <span className="info-value">{truncateText(candidate.cv.personal_info.competence, 70)}</span>
                                  </div>
                                )}
                                {candidate.planned_available_capacity && (
                                  <AvailabilityPreview availabilityData={candidate.planned_available_capacity} />
                                )}
                              </div>
                              <button className="expand-button"><span>View Profile</span></button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            }
          </div>
        )}
      </div>

      <CandidateCard
        activeCandidate={activeCandidate}
        modalVisible={modalVisible}
        closeCandidateModal={closeCandidateModal}
        getMatchScore={getMatchScore}
        staticCandidates={staticCandidates}
      />

      <div className="action-bar">
        <span className="selection-count">
          {totalCandidates} {totalCandidates === 1 ? 'candidate' : 'candidates'} found
        </span>
        <div className="action-buttons">
          <Button
            icon={<ArrowLeftOutlined />}
            className="button button-secondary"
            onClick={resetComponent}
          >
            Back
          </Button>
        </div>
      </div>
    </>
  );
};

export default SearchCandidates;